import { Routes } from '@angular/router';
import { CanActivateFn, Router, RouterModule } from '@angular/router';
import {inject, NgModule} from "@angular/core";
import {EinstiegComponent} from "./einstieg/einstieg.component";
import {StepChapterComponent} from "../step/step.chapter.component";
import {VerbrauchsanalyseStartComponent} from "./verbrauchsanalyse/verbrauchsanalyse.start.component";
import {VerbrauchsanalyseStepComponent} from "./verbrauchsanalyse/verbrauchsanalyse.step.component";
import {BestandsaufnahmeStartComponent} from "./bestandsaufnahme/bestandsaufnahme.start.component";
import {BestandsaufnahmeStepComponent} from "./bestandsaufnahme/bestandsaufnahme.step.component";
import {VerbrauchsanalyseErgebnisComponent} from "./verbrauchsanalyse/verbrauchsanalyse.ergebnis.component";
import {SanierungComponent} from "./sanierung/sanierung.component";
import {PortalService} from "../services/portal.service";
import {AdvisorPagenotfoundComponent} from "../advisor.pagenotfound.component";
import {VerbrauchsanalyseModeratStepVerbrauchsdatenComponent} from "./verbrauchsanalyse/verbrauchsanalyse-moderat.step.verbrauchsdaten.component";
import {VerbrauchsanalyseModeratAusstiegComponent} from "./verbrauchsanalyse/verbrauchsanalyse-moderat.ausstieg.component";
import {WpcheckStartComponent} from './wpcheck/wpcheck.start.component';
import {ExternalEntryComponent} from "./external-entry/external-entry.component";
import {ModeratEnergyMapComponent} from './energy-map/moderat.energy.map.component';
import {ModeratEnergyMapDonatedComponent} from './energy-map/moderat.energy.map.donated.component';
import {FernwaermeErgebnisComponent} from "./fernwaerme/fernwaerme.ergebnis.component";

/**
 * check if the user is in the district heating flow
 */
export function isDistrictHeating(): boolean {
  try {
    const anfrage = JSON.parse(localStorage.getItem('moc-anfrage') || '{}');
    return anfrage?.interesse === 'fernwaerme';
  } catch {
    return false;
  }
}

/**
 * define a nav postfix to generate a differnet navigation textkey
 */
export function getNavPostfix(): string {
  return isDistrictHeating() ? '.fernwaerme' : '';
}

/**
 * the chapter info page 'start' is not part of the district heating flow,
 * redirect to the first question instead (e.g. when the chapter link in the navigation is clicked)
 */
const skipStartForDistrictHeating: CanActivateFn = (route, state) => {
  if(isDistrictHeating()) {
    return inject(Router).parseUrl(state.url.replace('/verbrauchsanalyse/start', '/verbrauchsanalyse/basisinformationen/1'));
  }
  return true;
};

/**
 * ignore some nav elements when in the wpcheck path
 * @param url
 */
function ignoreNavForWpCheck(url: string) {
  return url.indexOf('wpcheck') !== -1;
}

/**
 * ignore some nav elements when outside the wpcheck path
 * @param url
 */
function ignorNavOutsideWpCheck(url: string) {
  return url.indexOf('wpcheck') === -1;
}

/**
 * Routes for the moderat.
 * The default route leads to moderat here
 */
const routes: Routes = [
  { path: ':portalId/moderat', children: [
    // default step is 'einstieg'
    { path: '', redirectTo: 'einstieg', pathMatch: 'full' },
    // list of al available steps in the advisor
    { path: 'einstieg', component: EinstiegComponent},
    { path: 'kurz', component: EinstiegComponent, data: {version: 'short'}},
    { path: 'extern', component: ExternalEntryComponent},
    // first input section and result page
    { path: 'verbrauchsanalyse', component: StepChapterComponent, data: {ignoreInNav: ignoreNavForWpCheck, navPostfix: getNavPostfix}, children: [
      { path: 'start', component: VerbrauchsanalyseStartComponent, canActivate: [skipStartForDistrictHeating], data: {swipeCount: '1'}},

      { path: 'basisinformationen/1', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '2'}},
      { path: 'basisinformationen/2', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '3'}},
      // for interesse === 'fernwaerme' this route sits right after the address step (basisinformationen/2), see groups in VerbrauchsanalyseModeratForm
      { path: 'fernwaerme/ergebnis', component: FernwaermeErgebnisComponent, data: {swipeCount: '4', ignoreInNav: true}},
      { path: 'basisinformationen/3', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '5'}},
      { path: 'basisinformationen/4', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '6'}},
      { path: 'basisinformationen/5', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '7'}},
      { path: 'basisinformationen/6', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '8'}},
      { path: 'basisinformationen/7', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '9'}},
      { path: 'basisinformationen/8', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '10'}},
      { path: 'basisinformationen/9', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '11'}},

      { path: 'heizsystem/1', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '20'}},
      { path: 'heizsystem/2', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '21'}},
      { path: 'heizsystem/3', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '22'}},
      { path: 'heizsystem/4', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '23'}},
      { path: 'heizsystem/5', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '24'}},
      { path: 'heizsystem/6', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '25'}},
      { path: 'heizsystem/7', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '26'}},
      { path: 'heizsystem/8', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '27'}},

      { path: 'verbrauchsdaten', component: VerbrauchsanalyseModeratStepVerbrauchsdatenComponent, data: {swipeCount: '30'}},

      { path: 'ergebnis', component: VerbrauchsanalyseErgebnisComponent, data: {swipeCount: '99', advisor: 'moderat'}},

      { path: 'ausstieg', component: VerbrauchsanalyseModeratAusstiegComponent, data: {swipeCount: '40', ignoreInNav: true, isDeadEnd: true}},

      { path: '', redirectTo: 'start', pathMatch: 'full' },
    ]},
    // second input section and result page
    { path: 'bestandsaufnahme', component: StepChapterComponent, data: {ignoreInNav: ignoreNavForWpCheck}, children: [
      { path: 'start', component: BestandsaufnahmeStartComponent, data: {swipeCount: '1'}},

      { path: 'gebaeudedetails/1', component: BestandsaufnahmeStepComponent, data: {swipeCount: '41'}},
      { path: 'gebaeudedetails/2', component: BestandsaufnahmeStepComponent, data: {swipeCount: '42'}},
      { path: 'gebaeudedetails/3', component: BestandsaufnahmeStepComponent, data: {swipeCount: '43'}},
      { path: 'gebaeudedetails/4', component: BestandsaufnahmeStepComponent, data: {swipeCount: '44'}},
      { path: 'gebaeudedetails/5', component: BestandsaufnahmeStepComponent, data: {swipeCount: '45'}},
      { path: 'gebaeudedetails/6', component: BestandsaufnahmeStepComponent, data: {swipeCount: '46'}},
      { path: 'gebaeudedetails/7', component: BestandsaufnahmeStepComponent, data: {swipeCount: '47'}},

      { path: 'heizanlage/1', component: BestandsaufnahmeStepComponent, data: {swipeCount: '55'}},
      { path: 'heizanlage/2', component: BestandsaufnahmeStepComponent, data: {swipeCount: '56'}},
      { path: 'heizanlage/3', component: BestandsaufnahmeStepComponent, data: {swipeCount: '57'}},
      { path: 'heizanlage/4', component: BestandsaufnahmeStepComponent, data: {swipeCount: '58'}},
      { path: 'heizanlage/5', component: BestandsaufnahmeStepComponent, data: {swipeCount: '59'}},
      { path: 'heizanlage/6', component: BestandsaufnahmeStepComponent, data: {swipeCount: '60'}},
      { path: 'heizanlage/7', component: BestandsaufnahmeStepComponent, data: {swipeCount: '61'}},

      { path: 'daemmung/1', component: BestandsaufnahmeStepComponent, data: {swipeCount: '70'}},
      { path: 'daemmung/2', component: BestandsaufnahmeStepComponent, data: {swipeCount: '71'}},
      { path: 'daemmung/3', component: BestandsaufnahmeStepComponent, data: {swipeCount: '99'}},

      { path: 'energy-map', component: ModeratEnergyMapComponent, data: {swipeCount: '100', ignoreInNav: true}},
      { path: 'energy-map-donated', component: ModeratEnergyMapDonatedComponent, data: {swipeCount: '101', ignoreInNav: true}},

      { path: '', redirectTo: 'start', pathMatch: 'full'},
    ]},
    // first input section in wpcheck path
    { path: 'wpcheck', component: StepChapterComponent, data: {ignoreInNav: ignorNavOutsideWpCheck}, children: [
        { path: 'start', component: WpcheckStartComponent, data: {swipeCount: '1'}},
        { path: 'wpcbasisinformationen/1', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '2'}},
        { path: 'wpcbasisinformationen/2', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '3'}},
        { path: 'wpcheizsystem/1', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '5'}},
        { path: 'wpcheizsystem/2', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '6'}},
        { path: 'wpcheizsystem/3', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '7'}},
        { path: 'wpcheizsystem/4', component: VerbrauchsanalyseStepComponent, data: {swipeCount: '8'}},
        { path: 'wpcheizanlage/1', component: BestandsaufnahmeStepComponent, data: {swipeCount: '10'}},
        { path: 'wpcheizanlage/2', component: BestandsaufnahmeStepComponent, data: {swipeCount: '11'}},
        { path: 'wpcheizanlage/3', component: BestandsaufnahmeStepComponent, data: {swipeCount: '12'}},
        { path: 'wpcheizanlage/4', component: BestandsaufnahmeStepComponent, data: {swipeCount: '13'}},
        { path: 'verbrauchsdaten', component: VerbrauchsanalyseModeratStepVerbrauchsdatenComponent, data: {swipeCount: '15'}},
        { path: '', redirectTo: 'start', pathMatch: 'full'},
      ]
    },
    // last section with measures
    { path: 'sanierung', component: StepChapterComponent, data: {ignoreInNav: ignoreNavForWpCheck}, children: [
      { path: 'massnahmen', component: SanierungComponent, data: {swipeCount: '1'}},
      { path: '', redirectTo: 'massnahmen', pathMatch: 'full' },
    ]},
    // last section in wpcheck path
    { path: 'wpcheckergebnis', component: StepChapterComponent, data: {ignoreInNav: ignorNavOutsideWpCheck}, children: [
        { path: 'massnahmen', component: SanierungComponent, data: {swipeCount: '1'}},
        { path: '', redirectTo: 'massnahmen', pathMatch: 'full' },
    ]},
  ]},
  {path: ':portalId', redirectTo: PortalService.defaultPortal + '/moderat', pathMatch: 'full'},
  {path: 'moderat/', redirectTo: PortalService.defaultPortal + '/moderat', pathMatch: 'full'},
  {path: '', redirectTo: PortalService.defaultPortal + '/moderat', pathMatch: 'full'},
  {path: '**', pathMatch: 'full', component: AdvisorPagenotfoundComponent, data: {advisor: 'moderat'}},
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    anchorScrolling: 'enabled',
    scrollOffset: [0, 150],
    paramsInheritanceStrategy: 'always'
})],
  exports: [RouterModule]
})
export class ModeratRoutingModule { }
